import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiClient, unwrap } from '../api/client.ts';
import type { PlatformMapFramework } from '../pages/platform-map-draft.ts';

/**
 * View-model hook for the setup → platform-map "Save & Rerun".
 *
 * Posts the edited framework draft to the genesis-rewrite endpoint and,
 * on success, invalidates the catalog + every view query so the preview
 * (and the rest of the app) reflects the rewritten, re-derived state.
 */

export interface RewritePlatformMapResponse {
  rewrite_id: number;
  genesis_at: string;
  status: 're-derived' | 'backfill-spawned';
  catalog: {
    skills_emitted: number;
    derived_contexts_emitted: number;
    business_functions_emitted: number;
    clusters_updated: number;
    clusters_deleted: number;
    fingerprints: number;
  };
  staleness: { backfilled: boolean; spawned_ingest_run_ids: string[] };
  derive: {
    observations_inserted: number;
    scores_inserted: number;
    engineers: number;
    derivedContexts: number;
  } | null;
}

function toBody(framework: PlatformMapFramework) {
  return {
    derivedContexts: framework.derivedContexts.map((c) => ({
      id: c.id,
      display: c.display,
      ...(c.subtitle != null ? { subtitle: c.subtitle } : {}),
      skills: c.skills.map((s) => ({ id: s.id, display: s.display, kind: s.kind })),
      functions: c.functions,
    })),
    business_functions: framework.businessFunctions.map((f) => ({
      id: f.id,
      display: f.display,
      ...(f.subtitle != null ? { subtitle: f.subtitle } : {}),
      // parentFunctionId references a sibling's `id`, so it round-trips in
      // whatever id form the draft holds (the rewrite endpoint stores it
      // verbatim, matching the sibling function ids).
      ...(f.parentFunctionId != null ? { parent_function_id: f.parentFunctionId } : {}),
    })),
  };
}

export function useRewritePlatformMap(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (framework: PlatformMapFramework) => {
      const res = await apiClient.v1.orgs[':slug']['platform-map'].rewrite.$post({
        param: { slug: orgSlug },
        json: toBody(framework),
      });
      return unwrap<RewritePlatformMapResponse>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['derivedContexts', orgSlug] });
      void qc.invalidateQueries({ queryKey: ['business_functions', orgSlug] });
      // All matrix/timeline views are re-derived — drop their caches.
      void qc.invalidateQueries({ queryKey: ['view'] });
    },
  });
}
